import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Avatar from "./Avatar";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

function FriendsList({ friends }) {
  if (friends === null) return <p className="py-4 text-sm text-[var(--text-secondary)]" data-testid="loved-by-dialog-loading">Loading…</p>;
  if (!friends.length) return <p className="py-4 text-sm text-[var(--text-secondary)]" data-testid="loved-by-dialog-empty">No friends to show.</p>;
  return (
    <ul className="max-h-[60vh] divide-y divide-[var(--border-subtle)] overflow-y-auto" data-testid="loved-by-dialog-list">
      {friends.map((f) => (
        <li key={f.id}>
          <Link to={f.username ? `/profile/${f.username}` : "#"} className="flex items-center gap-3 py-3 hover:text-[var(--accent-hover)]" data-testid={`loved-by-friend-${f.id}`}>
            <Avatar user={f} size={40} />
            <span className="min-w-0">
              <span className="block truncate font-semibold text-[var(--text-primary)]">{f.name}</span>
              {f.username && <span className="block truncate text-xs text-[var(--text-muted)]">@{f.username}</span>}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export default function LovedByStack({ item }) {
  const [open, setOpen] = useState(false);
  const [friends, setFriends] = useState(null);
  const people = item.loved_by || [];
  if (!people.length) return null;
  const extra = (item.friend_fav_count || people.length) - people.length;
  const names = people.map((p) => p.name).join(", ");
  const show = (e) => {
    e.preventDefault(); e.stopPropagation();
    setOpen(true); setFriends(null);
    api.get(`/catalog/loved-by-friends/${encodeURIComponent(item.id)}`)
      .then(({ data }) => setFriends(data.friends || []))
      .catch(() => setFriends(people));
  };
  return (
    <>
      <button type="button" onClick={show} aria-label={`Loved by ${names}${extra > 0 ? ` and ${extra} more` : ""}`} title={`Loved by ${names}`}
        className="absolute left-1.5 top-10 z-30 flex items-center rounded-full transition-transform hover:scale-105 active:scale-95" data-testid={`loved-by-${item.id}`}>
        {people.map((p, i) => (
          <Avatar key={p.id} user={p} size={26} className={`border-2 border-[var(--bg-base)] shadow ${i ? "-ml-2" : ""}`} testid={`loved-by-avatar-${item.id}-${p.id}`} />
        ))}
        {extra > 0 && <span className="-ml-2 flex h-[26px] min-w-[26px] items-center justify-center rounded-full border-2 border-[var(--bg-base)] bg-black/80 px-1 text-[10px] font-bold text-white">+{extra}</span>}
      </button>
      <LovedByDialog item={item} open={open} onOpenChange={setOpen} friends={friends} />
    </>
  );
}

// Shared "who loved this" dialog. Pass `friends` to control it, or omit to self-fetch on open.
export function LovedByDialog({ item, open, onOpenChange, friends: given }) {
  const [own, setOwn] = useState(null);
  const id = item?.id;
  useEffect(() => {
    if (given !== undefined || !open || !id) return;
    setOwn(null);
    api.get(`/catalog/loved-by-friends/${encodeURIComponent(id)}`)
      .then(({ data }) => setOwn(data.friends || []))
      .catch(() => setOwn(item.loved_by || []));
  }, [open, id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm border-[var(--border-subtle)] bg-[var(--bg-surface)] text-[var(--text-primary)]" data-testid="loved-by-dialog">
        <DialogHeader>
          <DialogTitle>Loved by Friends</DialogTitle>
          <DialogDescription className="text-[var(--text-secondary)]">Friends who favorited {item?.title}</DialogDescription>
        </DialogHeader>
        <FriendsList friends={given !== undefined ? given : own} />
      </DialogContent>
    </Dialog>
  );
}
